import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { dispatch, store } from 'store';
import { updateUserConfig } from 'store/slices/userConfig';
import { updateCompanyCustomViewVisibility } from 'api/custom-views';
import type { ApplicationUser } from 'types/company/applicationUser';
import type { CustomView } from 'types/userConfig/userConfig';
import { isApplicationUserVisibleInList } from 'utils/application-users';
import ColoredLetterAvatar from 'ui-component/display/ColoredLetterAvatar';
import ViewVisibilitySwitchWithHint from './ViewVisibilitySwitchWithHint';

type CustomViewVisibilityDialogProps = {
  open: boolean;
  onClose: () => void;
  view: CustomView | null;
  applicationUsers: ApplicationUser[];
};

const CustomViewVisibilityDialog: React.FC<CustomViewVisibilityDialogProps> = ({
  open,
  onClose,
  view,
  applicationUsers,
}) => {
  const intl = useIntl();
  const [saveHintByUdid, setSaveHintByUdid] = React.useState<Record<string, true>>({});

  const sortedUsers = React.useMemo(() => {
    return [...applicationUsers]
      .filter((u) => u.Udid)
      // רק משתמשים המסומנים "מוצג ברשימת משתמשים" (מסך /users) — כמו במטריצות התצוגות וההרשאות
      .filter(isApplicationUserVisibleInList)
      .sort((a, b) => (a.FullName || a.UserName || '').localeCompare(b.FullName || b.UserName || '', undefined, {
        sensitivity: 'base',
      }));
  }, [applicationUsers]);

  // רשימה לבנה (CompanyCustomViews): מסומן = המשתמש רואה את התצוגה
  const setVisibleForUser = React.useCallback(
    async (udid: string, visibleToUser: boolean) => {
      if (!view) return;
      const current = store.getState().userConfig.customViews.find((v) => v.id === view.id) ?? view;
      const visible = new Set(current.visibleUserUdids ?? []);
      if (visibleToUser) visible.add(udid);
      else visible.delete(udid);
      let ok = true;
      try {
        const updated = await updateCompanyCustomViewVisibility(String(view.id), [...visible]);
        const newViews = store.getState().userConfig.customViews.map((v) =>
          v.id === view.id ? { ...v, visibleUserUdids: updated.visibleUserUdids } : v,
        );
        dispatch(updateUserConfig({ customViews: newViews }));
      } catch {
        ok = false;
      }
      if (ok) {
        setSaveHintByUdid((f) => ({ ...f, [udid]: true }));
        window.setTimeout(() => {
          setSaveHintByUdid((f) => {
            const n = { ...f };
            delete n[udid];
            return n;
          });
        }, 2000);
      }
    },
    [view],
  );

  if (!view) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>
        <Stack spacing={0.5}>
          <Typography variant="h4" component="span">
            <FormattedMessage id="chat.sidebarCustomViews.visibilityDialogTitle" />
          </Typography>
          <Typography variant="body2" color="text.secondary">
            «{view.name}»
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          <FormattedMessage id="chat.sidebarCustomViews.visibilityDialogSubtitle" />
        </Typography>
        <List dense disablePadding>
          {sortedUsers.map((u) => {
            const udid = u.Udid as string;
            const visible = view.visibleUserUdids?.includes(udid) ?? false;
            const label = u.FullName || u.UserName || udid;
            return (
              <ListItem
                key={udid}
                secondaryAction={
                  <ViewVisibilitySwitchWithHint
                    checked={visible}
                    onChange={(checked) => void setVisibleForUser(udid, checked)}
                    showSavedHint={Boolean(saveHintByUdid[udid])}
                    inputProps={{
                      'aria-label': intl.formatMessage({ id: 'chat.sidebarCustomViews.toggleShowView' }),
                    }}
                  />
                }
                sx={{ pr: 10, py: 0.75 }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                  <ColoredLetterAvatar name={label} sx={{ width: 32, height: 32, fontSize: 14 }} />
                  <ListItemText primary={label} primaryTypographyProps={{ noWrap: true }} />
                </Stack>
              </ListItem>
            );
          })}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          <FormattedMessage id="chat.sidebarCustomViews.close" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomViewVisibilityDialog;
